/**
 * Class → equipment eligibility (M7-B).
 *
 * Restrictions apply on equip only — shared inventory never splits by class.
 * Only Vanguard is playable today; other class rows are documented from GDD
 * but unused until those classes ship (do not invent new permissions).
 *
 * Source: ROLLBREAK_GAME_DESIGN.md weapon permissions table.
 * Armour / rings / special die: not restricted by that table → all classes OK.
 */

import type { ItemDef, WeaponFamily } from './equipment';

export type WeaponPermission = {
  oneHand: readonly WeaponFamily[];
  twoHand: readonly WeaponFamily[];
  shield: boolean;
  magicalFocus: boolean;
};

/**
 * Confirmed GDD permissions. Only `vanguard` is an active ClassId today.
 */
export const CLASS_WEAPON_PERMISSIONS: Record<string, WeaponPermission> = {
  vanguard: {
    oneHand: ['sword', 'dagger', 'axe', 'mace'],
    twoHand: ['greatsword', 'greataxe', 'warhammer', 'bow'],
    shield: true,
    magicalFocus: false,
  },
  // Future classes (GDD) — not in PermanentSave.ClassId yet:
  shade: {
    oneHand: ['sword', 'dagger', 'axe', 'mace'],
    twoHand: ['greatsword', 'scythe', 'bow'],
    shield: false,
    magicalFocus: true,
  },
  arcanist: {
    oneHand: ['sword', 'dagger', 'wand'],
    twoHand: ['staff', 'bow'],
    shield: true,
    magicalFocus: true,
  },
  sentinel: {
    oneHand: ['sword', 'dagger', 'axe', 'mace', 'wand'],
    twoHand: ['greatsword', 'greataxe', 'warhammer', 'staff'],
    shield: true,
    magicalFocus: true,
  },
  soulbinder: {
    oneHand: ['sword', 'dagger', 'wand'],
    twoHand: ['staff', 'scythe'],
    shield: true,
    magicalFocus: true,
  },
  fateweaver: {
    oneHand: ['sword', 'dagger', 'mace', 'wand'],
    twoHand: ['warhammer', 'staff', 'bow'],
    shield: true,
    magicalFocus: true,
  },
};

export type ClassEquipRejectReason =
  | 'class_weapon_forbidden'
  | 'class_shield_forbidden'
  | 'class_focus_forbidden'
  | 'unknown_class_permissions';

export function canClassEquipItem(
  classId: string,
  def: ItemDef,
): { ok: true } | { ok: false; reason: ClassEquipRejectReason; detail: string } {
  // Non-weapon gear: no class gate in current design docs.
  if (def.category !== 'weapon') {
    return { ok: true };
  }

  const perms = CLASS_WEAPON_PERMISSIONS[classId];
  if (!perms) {
    return {
      ok: false,
      reason: 'unknown_class_permissions',
      detail: `No equipment permissions configured for class "${classId}"`,
    };
  }

  if (def.isShield) {
    if (!perms.shield) {
      return {
        ok: false,
        reason: 'class_shield_forbidden',
        detail: `${classId} cannot equip shields`,
      };
    }
    return { ok: true };
  }

  if (def.isMagicalFocus) {
    if (!perms.magicalFocus) {
      return {
        ok: false,
        reason: 'class_focus_forbidden',
        detail: `${classId} cannot equip magical foci`,
      };
    }
    // Magical focus still needs family check when family set.
  }

  const family = def.weaponFamily;
  if (!family) {
    // Weapon without family — treat as unrestricted structural fallback.
    return { ok: true };
  }

  const handedness = def.handedness ?? 'one_hand';
  const allowed =
    handedness === 'two_hand' ? perms.twoHand : perms.oneHand;
  if (!allowed.includes(family)) {
    return {
      ok: false,
      reason: 'class_weapon_forbidden',
      detail: `${classId} cannot equip ${family} (${handedness})`,
    };
  }

  if (def.isMagicalFocus && !perms.magicalFocus) {
    return {
      ok: false,
      reason: 'class_focus_forbidden',
      detail: `${classId} cannot equip magical foci`,
    };
  }

  return { ok: true };
}
